import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { List } from "@/components/list/List";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";
import { Module, PATH_PAYROLL } from "@/constants";
import { PayrollCreateButton } from "@/modules/payrolls/components/PayrollCreateButton";
import { usePayrollDetails } from "@/modules/payrolls/hooks/usePayrollDetails";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";
import { payrollDepartmentBasedInspectionPreview } from "@/schemas/payrollDepartmentBasedInspection";
import { payrollRoleBasedInspectionPreview } from "@/schemas/payrollRoleBasedInspection";
import { FetchListRequest } from "@/services/api";
import { PayrollsService } from "@/services/payrolls";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Navigate } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollPreviewColumnOverrides } from "../hooks/usePayrollPreviewColumnOverrides";
import { usePayrollQuery } from "../hooks/usePayrollQuery";
import { initialColumnVisibility } from "../initialColumnVisibility";
import { PayrollInspectionPreview } from "../types";
import { PayrollRecordView } from "./PayrollRecordView";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { PayrollActionLinks } from "./PayrollActionLinks";
import { PayrollCorrectionButton } from "./PayrollCorrectionButton";

const payrollRoleBasedInspectionPreviewDisplaySchema =
  payrollRoleBasedInspectionPreview
    .unwrap()
    .omit({
      contractor_card_mimetype: true,
      contractor_card_filename: true,
    })
    .readonly();

export const PayrollPreviewDirectory = memo(() => {
  const { t } = useTranslation();

  const { payrollId } = usePayrollParams();
  const payrollQuery = usePayrollQuery(payrollId);
  const isRoleBased = isProjectRoleDependent(payrollQuery.data);

  const permissions = usePayrollPermissions();

  const fetchListFn = useCallback(
    (params: FetchListRequest<PayrollInspectionPreview>) =>
      PayrollsService.getPreviewList(
        payrollId,
        payrollQuery.data?.project_depends_on,
        params,
      ),
    [payrollId, payrollQuery.data?.project_depends_on],
  );

  const { name, options, query, queryValue } =
    usePayrollDetails<PayrollInspectionPreview>({
      enabled: payrollQuery.isSuccess,
      fetchListFn,
      nameSuffix: "preview",
    });

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: t("payrolls.views.preview.title"),
    },
  ]);

  const schema = isRoleBased
    ? payrollRoleBasedInspectionPreviewDisplaySchema
    : payrollDepartmentBasedInspectionPreview;

  const columnOverrides = usePayrollPreviewColumnOverrides(isRoleBased);

  if (payrollQuery.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  if (!permissions.canAccessPreview) {
    return (
      <Navigate
        to={generatePath(PATH_PAYROLL, {
          payrollId,
        })}
        replace
      />
    );
  }

  return (
    <List
      schema={schema}
      name={name}
      query={query}
      options={options}
      columnOverrides={columnOverrides}
      tableFeatures={{
        initialColumnVisibility,
        exportSettings: {
          moduleName: Module.Payrolls,
          queryParams: {
            payroll_stage: isRoleBased
              ? "preview-role-based"
              : "preview-department-based",
            payroll_id: payrollId,
          },
        },
      }}
    >
      <List.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={[
          permissions.canCorrect && <PayrollCorrectionButton />,
          <PayrollCreateButton
            projectDependsOn={payrollQuery.data?.project_depends_on}
          />,
        ]}
      />
      <Title>{t("payrolls.views.preview.title")}</Title>
      <PayrollRecordView
        className="mb-4"
        actions={[
          <PayrollActionLinks
            aggregateOptions={{ isGrayed: true, isDisabled: true }}
            inspectOptions={{ isGrayed: true, isDisabled: true }}
            splitOptions={{ isGrayed: true, isDisabled: true }}
          />,
        ]}
      />
      <List.Menubar>
        <List.Menubar.Tools />
        <List.Menubar.Columns />
        <List.Menubar.Reset />
      </List.Menubar>
      <DataTableQueryContextProvider value={queryValue}>
        <List.Content>
          <List.Status />
        </List.Content>
      </DataTableQueryContextProvider>
    </List>
  );
});
